import { useEffect, useMemo, useRef, useState } from 'react'
import type { GeometryResponse } from '@xingroute/schema'
import { useLocale } from '../locale/LocaleContext.js'
import { trackBounds } from '../lib/gpx.js'
import {
  estimateBytes,
  enumerateTiles,
  expandBboxByKm,
  mapboxRasterTileUrl,
  prefetchTiles,
  withStyle,
  type BBox,
  type OfflinePackStyle,
  type PrefetchProgress,
} from '../lib/offline-tiles.js'
import { formatBytes } from '../lib/formatBytes.js'
import { putOfflinePack } from '../lib/offline-pack-db.js'
import type { MapStyle } from './RouteMap.js'
import { Button } from './ui/Button.js'

const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_KEY ?? '').trim()
const WARN_BYTES = 80 * 1024 * 1024
const REFUSE_BYTES = 200 * 1024 * 1024
const STYLE_CHOICES: OfflinePackStyle[] = ['standard', 'outdoor', 'satellite']

export function OfflineTileButton(_props: {
  routeId: string
  routeTitle: string
  geometry: GeometryResponse | null
  activeStyle: MapStyle
}) {
  return null
}

export function OfflineTileDialog({
  routeId,
  routeTitle,
  geometry,
  activeStyle,
  onClose,
}: {
  routeId: string
  routeTitle: string
  geometry: GeometryResponse | null
  activeStyle: MapStyle
  onClose: () => void
}) {
  const { t, locale } = useLocale()
  const [bufferKm, setBufferKm] = useState(0.5)
  const [zoomMin, setZoomMin] = useState(12)
  const [zoomMax, setZoomMax] = useState(16)
  const [styles, setStyles] = useState<OfflinePackStyle[]>(() => [
    activeStyle === 'satellite' || activeStyle === 'outdoor' ? activeStyle : 'standard',
  ])
  const [progress, setProgress] = useState<PrefetchProgress | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const baseBbox = useMemo(() => geometryBbox(geometry), [geometry])
  const bufferedBbox = useMemo(
    () => (baseBbox ? expandBboxByKm(baseBbox, bufferKm) : null),
    [baseBbox, bufferKm],
  )
  const zoomRange = normalizeZoomRange(zoomMin, zoomMax)
  const baseTiles = useMemo(
    () => (bufferedBbox ? estimateTileSpecs(bufferedBbox, zoomRange.min, zoomRange.max) : []),
    [bufferedBbox, zoomRange.min, zoomRange.max],
  )
  const tileSpecs = useMemo(
    () => styles.flatMap((style) => withStyle(baseTiles, style)),
    [baseTiles, styles],
  )
  const estimate = useMemo(() => estimateBytes(tileSpecs), [tileSpecs])
  const refused = estimate.bytes > REFUSE_BYTES
  const warned = estimate.bytes > WARN_BYTES && !refused

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  function toggleStyle(style: OfflinePackStyle) {
    setStyles((prev) => {
      if (prev.includes(style))
        return prev.length === 1 ? prev : prev.filter((item) => item !== style)
      return [...prev, style]
    })
  }

  async function startDownload() {
    if (!bufferedBbox || refused || busy || !MAPBOX_TOKEN || tileSpecs.length === 0) return
    const controller = new AbortController()
    abortRef.current = controller
    setBusy(true)
    setDone(false)
    setError(null)
    setProgress({ done: 0, total: tileSpecs.length, bytes: 0, skipped: 0 })
    try {
      let finalProgress: PrefetchProgress = {
        done: 0,
        total: tileSpecs.length,
        bytes: 0,
        skipped: 0,
      }
      for await (const next of prefetchTiles(
        tileSpecs,
        (spec) => mapboxRasterTileUrl(spec, MAPBOX_TOKEN, routeId),
        { concurrency: 4, signal: controller.signal },
      )) {
        finalProgress = next
        setProgress(next)
      }
      const downloadedAt = new Date().toISOString()
      await Promise.all(
        styles.map((style) => {
          const count = baseTiles.length
          const share = tileSpecs.length > 0 ? count / tileSpecs.length : 0
          return putOfflinePack({
            route_id: routeId,
            bbox: bufferedBbox,
            zoom_min: zoomRange.min,
            zoom_max: zoomRange.max,
            style,
            tile_count: count,
            byte_count: Math.round(finalProgress.bytes * share),
            downloaded_at: downloadedAt,
          })
        }),
      )
      setDone(true)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError(locale === 'zh' ? '已取消下载。' : 'Download cancelled.')
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setBusy(false)
      abortRef.current = null
    }
  }

  function cancelDownload() {
    abortRef.current?.abort()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg space-y-4 rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 shadow-[var(--xr-shadow-md)]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="space-y-1">
          <h2 className="text-lg font-semibold text-[var(--xr-text-primary)]">
            {t('offline.download')}
          </h2>
          <p className="text-xs text-[var(--xr-text-muted)]">{routeTitle}</p>
        </header>

        {!baseBbox ? (
          <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)] p-3 text-sm text-[var(--xr-text-secondary)]">
            {locale === 'zh'
              ? '此线路还没有可用于离线瓦片的 GPX 几何。'
              : 'This route has no GPX geometry for offline tiles yet.'}
          </p>
        ) : (
          <>
            <div className="grid gap-3 text-sm text-[var(--xr-text-secondary)]">
              <label className="grid gap-1">
                <span>
                  {locale === 'zh' ? '缓冲距离' : 'Buffer'} · {bufferKm.toFixed(1)} km
                </span>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.1}
                  value={bufferKm}
                  disabled={busy}
                  onChange={(event) => setBufferKm(Number(event.target.value))}
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  label={locale === 'zh' ? '最小 zoom' : 'Min zoom'}
                  value={zoomMin}
                  min={10}
                  max={18}
                  disabled={busy}
                  onChange={setZoomMin}
                />
                <NumberField
                  label={locale === 'zh' ? '最大 zoom' : 'Max zoom'}
                  value={zoomMax}
                  min={10}
                  max={18}
                  disabled={busy}
                  onChange={setZoomMax}
                />
              </div>
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
                  Style
                </p>
                <div className="flex flex-wrap gap-2">
                  {STYLE_CHOICES.map((style) => (
                    <label
                      key={style}
                      className="inline-flex items-center gap-2 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] px-3 py-2 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={styles.includes(style)}
                        disabled={busy || (styles.length === 1 && styles.includes(style))}
                        onChange={() => toggleStyle(style)}
                      />
                      {styleLabel(style, locale)}
                    </label>
                  ))}
                </div>
              </div>
              <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-bg-surface-elev)] p-3 text-sm">
                {locale === 'zh' ? '预计' : 'Estimated'} {estimate.count.toLocaleString()}{' '}
                {locale === 'zh' ? '块瓦片，约' : 'tiles, about'} {formatBytes(estimate.bytes)}
              </p>
              {warned && (
                <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-warning-soft)] p-3 text-sm text-[var(--xr-warning)]">
                  {t('offline.estimate.warn')}
                </p>
              )}
              {refused && (
                <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-danger-soft)] p-3 text-sm text-[var(--xr-danger)]">
                  {t('offline.estimate.refuse')}
                </p>
              )}
              {!MAPBOX_TOKEN && (
                <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-danger-soft)] p-3 text-sm text-[var(--xr-danger)]">
                  VITE_MAPBOX_KEY missing.
                </p>
              )}
            </div>

            {progress && (
              <div className="space-y-2">
                <div className="h-2 overflow-hidden rounded-full bg-[var(--xr-bg-surface-elev)]">
                  <div
                    className="h-full bg-[var(--xr-brand-primary)] transition-all"
                    style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-[var(--xr-text-muted)]">
                  {progress.done} / {progress.total} · {formatBytes(progress.bytes)}
                  {progress.skipped > 0
                    ? ` · ${
                        locale === 'zh'
                          ? `跳过 ${progress.skipped} 个瓦片`
                          : `${progress.skipped} tiles skipped`
                      }`
                    : ''}
                </p>
              </div>
            )}
            {done && (
              <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-brand-primary-soft)] p-3 text-sm text-[var(--xr-brand-primary)]">
                ✓ {t('offline.installed')} · {formatBytes(progress?.bytes ?? 0)}
              </p>
            )}
            {error && (
              <p className="rounded-[var(--xr-radius-md)] bg-[var(--xr-danger-soft)] p-3 text-sm text-[var(--xr-danger)]">
                {error}
              </p>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={busy ? cancelDownload : onClose}>
            {busy ? t('cancel') : locale === 'zh' ? '关闭' : 'Close'}
          </Button>
          <Button
            type="button"
            onClick={() => void startDownload()}
            disabled={!baseBbox || busy || refused || !MAPBOX_TOKEN || tileSpecs.length === 0}
          >
            {busy ? (locale === 'zh' ? '下载中…' : 'Downloading…') : t('offline.download')}
          </Button>
        </div>
      </div>
    </div>
  )
}

function NumberField({
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  disabled: boolean
  onChange: (value: number) => void
}) {
  return (
    <label className="grid gap-1">
      <span>{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        className="h-9 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 text-sm text-[var(--xr-text-primary)]"
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  )
}

function geometryBbox(geometry: GeometryResponse | null): BBox | null {
  const coords = geometry ? (geometry.line.length > 0 ? geometry.line : geometry.points) : []
  if (coords.length === 0) return null
  const bounds = trackBounds(
    coords.map((p) => ({
      lat: p.lat,
      lon: p.lon,
      ele: p.ele,
    })),
  )
  if (!bounds) return null
  return [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]]
}

function estimateTileSpecs(bbox: BBox, min: number, max: number) {
  return enumerateTiles(bbox, { min, max })
}

function normalizeZoomRange(a: number, b: number) {
  const min = Math.max(10, Math.min(18, Math.floor(Math.min(a, b))))
  const max = Math.max(min, Math.min(18, Math.floor(Math.max(a, b))))
  return { min, max }
}

function styleLabel(style: OfflinePackStyle, locale: 'zh' | 'en') {
  if (locale === 'zh') {
    if (style === 'standard') return '标准'
    if (style === 'outdoor') return '户外'
    return '卫星'
  }
  if (style === 'standard') return 'Standard'
  if (style === 'outdoor') return 'Outdoor'
  return 'Satellite'
}
